import { cloneDeep, isEqual } from "es-toolkit";
import { computed, MaybeRefOrGetter, reactive, ref, toValue, watch, type ComputedRef, type Ref } from "vue";

import { UpdateProgramRequest } from "@/domains/v4b/program/program-update.types";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { defaultProgramColorName, ProgramColorName, ProgramDetails } from "@/domains/v4b/program/program.types";
import { SubprogramsByApplianceCategory } from "@/domains/v4b/program/subprogram.types";
import { keys } from "@/utils/object";
import {
  applyQuickConfigurationsProgram,
  createOffProgram,
  TProgramQuickConfiguration,
} from "@/utils/programs/program-presets";
import { getProgramApplianceCategories } from "@/utils/programs/program-utils";

interface IUseProgramEditorOptions {
  programDetails: MaybeRefOrGetter<ProgramDetails | undefined>;
  installedApplianceCategories: MaybeRefOrGetter<readonly EProgramApplianceCategory[] | undefined>;
  defaultApplianceCategories: readonly EProgramApplianceCategory[];
  blackListedApplianceCategories?: readonly EProgramApplianceCategory[];
  validateInitialSubPrograms?: MaybeRefOrGetter<boolean>;
  savePending?: MaybeRefOrGetter<boolean>;
}

interface IProgramEditorSaveHandlers {
  updateProgram: (request: UpdateProgramRequest) => Promise<unknown>;
  saveSubPrograms: (subPrograms: SubprogramsByApplianceCategory) => Promise<unknown>;
}

export interface IProgramEditor {
  name: Ref<string>;
  programColor: Ref<ProgramColorName>;
  subPrograms: Ref<SubprogramsByApplianceCategory | undefined>;
  activeApplianceCategory: Ref<EProgramApplianceCategory>;
  activeQuickConfigurations: ComputedRef<readonly TProgramQuickConfiguration[]>;
  validatedApplianceCategories: Ref<EProgramApplianceCategory[]>;
  displayedApplianceCategories: ComputedRef<EProgramApplianceCategory[]>;
  isInitialized: ComputedRef<boolean>;
  isNameValid: ComputedRef<boolean>;
  isConfigurationComplete: ComputedRef<boolean>;
  hasNameChanges: ComputedRef<boolean>;
  hasSubProgramChanges: ComputedRef<boolean>;
  isDirty: ComputedRef<boolean>;
  canSave: ComputedRef<boolean>;
  validateActiveApplianceCategory: () => void;
  validateActiveApplianceCategoryAndSelectNext: () => boolean;
  toggleQuickConfiguration: (configuration: TProgramQuickConfiguration) => void;
  handleManualProgramChange: (category: EProgramApplianceCategory) => void;
  discardChanges: () => void;
  save: (handlers: IProgramEditorSaveHandlers) => Promise<boolean>;
}

function hasOwnProgram(
  subPrograms: SubprogramsByApplianceCategory | undefined,
  category: EProgramApplianceCategory,
): boolean {
  return !!subPrograms && Object.prototype.hasOwnProperty.call(subPrograms, category);
}

export function useProgramEditor(options: IUseProgramEditorOptions): IProgramEditor {
  const blackListedApplianceCategories = options.blackListedApplianceCategories ?? [];
  const name = ref("");
  const programColor = ref<ProgramColorName>(defaultProgramColorName);
  const subPrograms = ref<SubprogramsByApplianceCategory>() as Ref<SubprogramsByApplianceCategory | undefined>;
  const initialName = ref<string>();
  const initialProgramColor = ref<ProgramColorName>(defaultProgramColorName);
  const initialSubPrograms = ref<SubprogramsByApplianceCategory>() as Ref<SubprogramsByApplianceCategory | undefined>;
  const initializedProgramId = ref<number>();
  const initializedProgramSiteId = ref<number>();
  const activeApplianceCategory = ref<EProgramApplianceCategory>(options.defaultApplianceCategories[0]);
  const validatedApplianceCategories = ref<EProgramApplianceCategory[]>([]);
  const quickConfigurationsByCategory = reactive<
    Partial<Record<EProgramApplianceCategory, TProgramQuickConfiguration[]>>
  >({});
  const quickConfigurationBasePrograms = reactive<SubprogramsByApplianceCategory>({});
  const quickConfigurationBaseProgramPresence = reactive<Partial<Record<EProgramApplianceCategory, boolean>>>({});
  const quickConfigurationRevisions: Partial<Record<EProgramApplianceCategory, number>> = {};
  let editorGeneration = 0;

  const displayedApplianceCategories = computed<EProgramApplianceCategory[]>(() => {
    return getProgramApplianceCategories(
      options.defaultApplianceCategories,
      toValue(options.installedApplianceCategories),
      blackListedApplianceCategories,
    );
  });

  const isInitialized = computed(() => initializedProgramId.value !== undefined);
  const activeQuickConfigurations = computed<readonly TProgramQuickConfiguration[]>(() => [
    ...(quickConfigurationsByCategory[activeApplianceCategory.value] ?? []),
  ]);
  const isNameValid = computed(() => name.value.trim().length > 0);
  const isNameDirty = computed(() => isInitialized.value && name.value !== initialName.value);
  const isProgramColorDirty = computed<boolean>(
    () => isInitialized.value && programColor.value !== initialProgramColor.value,
  );
  const hasSubProgramChanges = computed(
    () => isInitialized.value && !isEqual(subPrograms.value, initialSubPrograms.value),
  );
  const isDirty = computed(() => isNameDirty.value || hasSubProgramChanges.value || isProgramColorDirty.value);
  const isConfigurationComplete = computed(() =>
    displayedApplianceCategories.value.every((category) => validatedApplianceCategories.value.includes(category)),
  );
  const canSave = computed(
    () =>
      isInitialized.value &&
      isNameValid.value &&
      isConfigurationComplete.value &&
      isDirty.value &&
      !toValue(options.savePending ?? false),
  );

  function hasInitiallyValidatedProgram(category: EProgramApplianceCategory): boolean {
    return toValue(options.validateInitialSubPrograms ?? true) && hasOwnProgram(initialSubPrograms.value, category);
  }

  function synchronizeValidatedApplianceCategories() {
    validatedApplianceCategories.value = displayedApplianceCategories.value.filter(
      (category) => validatedApplianceCategories.value.includes(category) || hasInitiallyValidatedProgram(category),
    );
  }

  function clearQuickConfigurationState(category?: EProgramApplianceCategory) {
    if (category !== undefined) {
      delete quickConfigurationsByCategory[category];
      delete quickConfigurationBasePrograms[category];
      delete quickConfigurationBaseProgramPresence[category];
      return;
    }

    for (const configuredCategory of keys(quickConfigurationsByCategory)) {
      delete quickConfigurationsByCategory[configuredCategory];
    }
    for (const basedCategory of keys(quickConfigurationBasePrograms)) {
      delete quickConfigurationBasePrograms[basedCategory];
    }
    for (const presentCategory of keys(quickConfigurationBaseProgramPresence)) {
      delete quickConfigurationBaseProgramPresence[presentCategory];
    }
  }

  function hasActiveQuickConfigurationState(): boolean {
    return Object.values(quickConfigurationsByCategory).some((configurations) => configurations?.length);
  }

  function clearQuickConfigurationRevisions() {
    for (const category of keys(quickConfigurationRevisions)) {
      delete quickConfigurationRevisions[category];
    }
  }

  function incrementQuickConfigurationRevision(category: EProgramApplianceCategory) {
    quickConfigurationRevisions[category] = (quickConfigurationRevisions[category] ?? 0) + 1;
  }

  function initialize(details: ProgramDetails) {
    const isSameProgram =
      initializedProgramId.value === details.id && initializedProgramSiteId.value === details.siteId;
    if (isSameProgram && (isDirty.value || hasActiveQuickConfigurationState())) return;

    editorGeneration += 1;
    clearQuickConfigurationState();
    clearQuickConfigurationRevisions();
    name.value = details.name;
    programColor.value = details.frontDisplayColor;
    subPrograms.value = cloneDeep(details.subprograms) as SubprogramsByApplianceCategory;
    initialName.value = details.name;
    initialProgramColor.value = details.frontDisplayColor ?? defaultProgramColorName;
    initialSubPrograms.value = cloneDeep(details.subprograms) as SubprogramsByApplianceCategory;
    initializedProgramId.value = details.id;
    initializedProgramSiteId.value = details.siteId;
    validatedApplianceCategories.value = displayedApplianceCategories.value.filter(hasInitiallyValidatedProgram);

    if (!isSameProgram || !displayedApplianceCategories.value.includes(activeApplianceCategory.value)) {
      activeApplianceCategory.value = displayedApplianceCategories.value[0];
    }
  }

  function reset() {
    editorGeneration += 1;
    clearQuickConfigurationState();
    clearQuickConfigurationRevisions();
    name.value = "";
    subPrograms.value = undefined;
    initialName.value = undefined;
    initialProgramColor.value = defaultProgramColorName;
    initialSubPrograms.value = undefined;
    initializedProgramId.value = undefined;
    initializedProgramSiteId.value = undefined;
    validatedApplianceCategories.value = [];
  }

  function validateActiveApplianceCategory() {
    const category = activeApplianceCategory.value;
    const currentSubPrograms = subPrograms.value ?? ({} as SubprogramsByApplianceCategory);

    if (quickConfigurationBasePrograms[category] && quickConfigurationBaseProgramPresence[category] === false) {
      quickConfigurationBasePrograms[category] = createOffProgram();
      quickConfigurationBaseProgramPresence[category] = true;
    }

    if (!hasOwnProgram(currentSubPrograms, category)) {
      subPrograms.value = {
        ...currentSubPrograms,
        [category]: createOffProgram(),
      };
    }

    if (!validatedApplianceCategories.value.includes(category)) {
      validatedApplianceCategories.value = displayedApplianceCategories.value.filter(
        (displayedCategory) =>
          validatedApplianceCategories.value.includes(displayedCategory) || displayedCategory === category,
      );
    }
  }

  function validateActiveApplianceCategoryAndSelectNext(): boolean {
    validateActiveApplianceCategory();

    const activeCategoryIndex = displayedApplianceCategories.value.indexOf(activeApplianceCategory.value);
    const nextCategory = displayedApplianceCategories.value[activeCategoryIndex + 1];

    if (!nextCategory) return false;

    activeApplianceCategory.value = nextCategory;
    return true;
  }

  function toggleQuickConfiguration(configuration: TProgramQuickConfiguration) {
    const category = activeApplianceCategory.value;
    const currentConfigurations = quickConfigurationsByCategory[category] ?? [];
    const updatedConfigurations = currentConfigurations.includes(configuration)
      ? currentConfigurations.filter((activeConfiguration) => activeConfiguration !== configuration)
      : [...currentConfigurations, configuration];

    let baseProgram = quickConfigurationBasePrograms[category];
    if (!baseProgram) {
      baseProgram = cloneDeep(subPrograms.value?.[category] ?? createOffProgram());
      quickConfigurationBasePrograms[category] = baseProgram;
      quickConfigurationBaseProgramPresence[category] = hasOwnProgram(subPrograms.value, category);
    }

    if (updatedConfigurations.length || quickConfigurationBaseProgramPresence[category]) {
      subPrograms.value = {
        ...(subPrograms.value ?? ({} as SubprogramsByApplianceCategory)),
        [category]: applyQuickConfigurationsProgram(baseProgram, category, updatedConfigurations),
      };
    } else {
      const restoredSubPrograms = { ...subPrograms.value };
      delete restoredSubPrograms[category];
      subPrograms.value = restoredSubPrograms as SubprogramsByApplianceCategory;
    }

    if (updatedConfigurations.length) {
      quickConfigurationsByCategory[category] = updatedConfigurations;
    } else {
      clearQuickConfigurationState(category);
    }
    incrementQuickConfigurationRevision(category);
  }

  function handleManualProgramChange(category: EProgramApplianceCategory) {
    clearQuickConfigurationState(category);
    incrementQuickConfigurationRevision(category);
  }

  function discardChanges() {
    if (!isInitialized.value) return;

    editorGeneration += 1;
    clearQuickConfigurationState();
    clearQuickConfigurationRevisions();
    name.value = initialName.value ?? "";
    programColor.value = initialProgramColor.value;
    subPrograms.value = cloneDeep(initialSubPrograms.value);
    validatedApplianceCategories.value = displayedApplianceCategories.value.filter(hasInitiallyValidatedProgram);
  }

  async function save(handlers: IProgramEditorSaveHandlers): Promise<boolean> {
    if (!canSave.value) return false;

    const updateProgram = handlers.updateProgram;
    const shouldUpdateProgram = (isNameDirty.value || isProgramColorDirty.value) && updateProgram !== undefined;
    const shouldSaveSubPrograms = hasSubProgramChanges.value;
    if (!shouldUpdateProgram && !shouldSaveSubPrograms) return false;
    if (isNameDirty.value && !isNameValid.value) return false;
    const submittedName = name.value;
    const submittedSubPrograms = cloneDeep(subPrograms.value) as SubprogramsByApplianceCategory;
    const submittedQuickConfigurationRevisions = new Map(
      (Object.keys(quickConfigurationsByCategory) as EProgramApplianceCategory[])
        .filter((category) => quickConfigurationsByCategory[category]?.length)
        .map((category) => [category, quickConfigurationRevisions[category] ?? 0] as const),
    );
    const submittedEditorGeneration = editorGeneration;

    if (shouldUpdateProgram) {
      await updateProgram({
        name: isNameDirty.value ? submittedName : undefined,
        frontDisplayColor: isProgramColorDirty ? programColor.value : undefined,
      });
      if (editorGeneration === submittedEditorGeneration) {
        initialName.value = submittedName;
        initialProgramColor.value = programColor.value;
      }
    }

    if (shouldSaveSubPrograms) {
      await handlers.saveSubPrograms(submittedSubPrograms);
      if (editorGeneration === submittedEditorGeneration) {
        initialSubPrograms.value = cloneDeep(submittedSubPrograms);
      }
    }

    if (editorGeneration === submittedEditorGeneration) {
      for (const [category, submittedRevision] of submittedQuickConfigurationRevisions) {
        if (quickConfigurationRevisions[category] === submittedRevision) {
          clearQuickConfigurationState(category);
        }
      }
    }
    return true;
  }

  watch(
    () => toValue(options.programDetails),
    (details) => {
      if (details) {
        initialize(details);
      } else {
        reset();
      }
    },
    { immediate: true, deep: true },
  );

  watch(displayedApplianceCategories, (categories) => {
    if (!categories.includes(activeApplianceCategory.value)) {
      activeApplianceCategory.value = categories[0];
    }
    synchronizeValidatedApplianceCategories();
  });

  return {
    name,
    programColor,
    subPrograms,
    activeApplianceCategory,
    activeQuickConfigurations,
    validatedApplianceCategories,
    displayedApplianceCategories,
    isInitialized,
    isNameValid,
    isConfigurationComplete,
    hasNameChanges: isNameDirty,
    hasSubProgramChanges,
    isDirty,
    canSave,
    validateActiveApplianceCategory,
    validateActiveApplianceCategoryAndSelectNext,
    toggleQuickConfiguration,
    handleManualProgramChange,
    discardChanges,
    save,
  };
}
